/**
 * Pure helpers for the channel app's message list (ChannelApp.jsx). The
 * backend (sdk_apps/channel.py) serves pages of root messages oldest first
 * and one thread's replies on request; these merge what a poll brings into
 * what the page already holds, without re-rendering an idle list.
 */

const sameReactions = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);

const sameMessage = (held, fresh) =>
    held.content === fresh.content &&
    held.edited_at === fresh.edited_at &&
    held.reply_count === fresh.reply_count &&
    held.last_reply_at === fresh.last_reply_at &&
    sameReactions(held.reactions, fresh.reactions);

const byTime = (a, b) => new Date(a.timestamp) - new Date(b.timestamp);

/**
 * Merge a freshly fetched page into the messages held. A held message the
 * page carries takes the page's content, reactions and reply counts (others
 * edit and react); messages the page does not carry are kept as they are
 * (they are older than the page, or were posted and not yet confirmed); new
 * ones are added. Returns the same array when nothing changed, so a poll that
 * brought nothing new is a no-op for React.
 */
export function mergeMessages(held, page) {
    if (!Array.isArray(page)) return held;
    const fresh = new Map(page.map(m => [m.message_id, m]));
    let changed = false;
    const refreshed = held.map(m => {
        const f = fresh.get(m.message_id);
        if (!f || sameMessage(m, f)) return m;
        changed = true;
        return { ...m, ...f };
    });
    const have = new Set(held.map(m => m.message_id));
    const added = page.filter(m => !have.has(m.message_id));
    if (!changed && added.length === 0) return held;
    return [...refreshed, ...added].sort(byTime);
}

/** Messages the server knows nothing about any more are dropped: the page
 *  covers their time, yet does not carry them (deleted by someone else). */
export function dropDeleted(held, page) {
    if (!Array.isArray(page) || page.length === 0) return held;
    const fresh = new Set(page.map(m => m.message_id));
    const oldest = new Date(page[0].timestamp);
    const kept = held.filter(m => fresh.has(m.message_id) || new Date(m.timestamp) < oldest || m.pending);
    return kept.length === held.length ? held : kept;
}

/** Swap a message by id; a replacer takes the held message and returns the
 *  new one. Returns the same array when the id is not held. */
export function replaceMessage(held, messageId, replacer) {
    let changed = false;
    const next = held.map(m => {
        if (m.message_id !== messageId) return m;
        changed = true;
        return replacer(m);
    });
    return changed ? next : held;
}

/** Group a message's flat reactions into chips, in first-reacted order. */
export function reactionGroups(reactions, userId) {
    const groups = [];
    for (const r of reactions || []) {
        let g = groups.find(x => x.emoji === r.emoji);
        if (!g) groups.push(g = { emoji: r.emoji, count: 0, mine: false });
        g.count += 1;
        if (r.user_id === userId) g.mine = true;
    }
    return groups;
}

/** The viewer's reactions toggled: added when absent, removed when present. */
export function toggleReactionRows(reactions, userId, emoji) {
    const rows = reactions || [];
    const mine = rows.some(r => r.emoji === emoji && r.user_id === userId);
    return mine ? rows.filter(r => !(r.emoji === emoji && r.user_id === userId)) : [...rows, { emoji, user_id: userId }];
}
